import { useMutation } from "@tanstack/react-query";
import {
  analyticsApi,
  AnalyticsChartDataResponse,
  AnalyticsFilterApiInputPayload,
  AnalyticQueuesResponse,
  AnalyticStoppedVehicleResponse,
  StoppedVehicleChartData,
  AnalyticsCategoryDataResponse,
  AnalyticsCategoryApiInputPayload,
  AnalyticsCategoryData,
  AnalyticsCSVDownloadPayload,
  AnalyticsDetailsResponse
} from "@queries/Analytics";
import { useMemo, useState } from "react";
import {
  ANALYTICS_CATEGORY_API,
  ANALYTICS_CHART_API,
  ANALYTICS_CHART_TYPE,
  ANALYTICS_CHART_VIEW_TYPE
} from "@resources/Constants";
import { generateUniqueColors } from "@utils/Global";
import { PALETTES } from "@resources/Palettes";

const chartColors = [
  PALETTES.DARK.SYSTEM_YELLOW,
  PALETTES.DARK.SYSTEM_GREEN,
  PALETTES.DARK.SYSTEM_BLUE,
  PALETTES.DARK.SYSTEM_ORANGE,
  PALETTES.DARK.SYSTEM_EMERALD,
  PALETTES.DARK.SYSTEM_CORAL,
  PALETTES.DARK.SYSTEM_INDIGO,
  PALETTES.DARK.SYSTEM_PURPLE,
  PALETTES.DARK.SYSTEM_RED,
  PALETTES.LIGHT.SYSTEM_PINK,
  PALETTES.DARK.SYSTEM_VIOLET,
  PALETTES.DARK.SYSTEM_SAPPHIRE
];

const locationChartColor: Record<string, string> = {};

const getLocationColor = (location: string) => {
  if (!locationChartColor[location]) {
    const usedColors = Object.values(locationChartColor);
    const uniqueColors = chartColors.filter(
      (color) => usedColors.indexOf(color) < 0
    );
    if (uniqueColors.length) {
      locationChartColor[location] = uniqueColors[0];
    } else {
      locationChartColor[location] = generateUniqueColors(1)[0];
    }
  }
  return locationChartColor[location];
};

export const useGetAnalyticsChartQuery = (
  widgetType: string,
  chartType?: string,
  selectedVehicleTypes?: string[],
  csvFileName?: string
) => {
  const [chartData, setChartData] = useState<
    (AnalyticsDetailsResponse & { key: string })[]
  >([]);
  const [categoryData, setCategoryData] = useState<AnalyticsCategoryData[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isCategoryDataLoading, setCategoryDataLoading] = useState(true);

  const [isFetched, setIsFetched] = useState(false);
  const [originalData, setOriginalData] = useState<AnalyticsChartDataResponse>(
    []
  );

  const calculateChartData = (
    data: AnalyticsChartDataResponse,
    widgetType: string
  ) => {
    const nameSet: Record<string, number> = {}; // 이름을 추적하는 객체
    const converted = data.map((item) => {
      const name = item.locationName;
      const key = (() => {
        if (nameSet[name]) {
          nameSet[name] += 1;
        } else {
          nameSet[name] = 1;
        }

        return `${name}-${nameSet[name]}`;
      })();

      return { ...item, key };
    });

    if (
      widgetType === ANALYTICS_CHART_TYPE.PEOPLE_QUEUES ||
      widgetType === ANALYTICS_CHART_TYPE.RETAIL_VISITOR_AREA_TRAFFIC ||
      widgetType === ANALYTICS_CHART_TYPE.SHOPPING_CART_QUEUES ||
      widgetType === ANALYTICS_CHART_TYPE.VEHICLE_QUEUES
    ) {
      const dataKey = chartType === "count" ? "count" : "time";
      return converted.map((location, i) => {
        const { locationName, chartData } = location as AnalyticQueuesResponse;
        const Data = chartData.map((point) => point[dataKey] as number);
        return {
          ...location,
          locationName,
          key: location.key,
          chartData: Data,
          originalData: chartData,
          color:
            chartColors.length >= i
              ? chartColors[i]
              : generateUniqueColors(1)[0]
        };
      });
    }
    if (widgetType === ANALYTICS_CHART_TYPE.STOPPED_VEHICLE_ANALYTICS) {
      return converted.map((location) => {
        const { locationName, chartData } =
          location as AnalyticStoppedVehicleResponse;

        let selectedData = selectedVehicleTypes?.map((item) =>
          item.toLowerCase()
        );
        if (selectedData?.includes("all")) {
          selectedData = Array.isArray(selectedData)
            ? selectedData.filter((item) => item !== "all")
            : [];
        }
        const keys = selectedData?.length
          ? selectedData
          : (Object.keys(
              chartData
            ) as (keyof AnalyticStoppedVehicleResponse["chartData"])[]);

        const chartSumByType = Array.from({
          length: chartData[keys[0] as keyof StoppedVehicleChartData].length
        }).map((_v, i) =>
          keys
            .map((key) => chartData[key as keyof StoppedVehicleChartData][i])
            .reduce((a, b) => a + b, 0)
        );

        return {
          locationName,
          key: location.key,
          chartData: chartSumByType,
          color: getLocationColor(locationName)
        };
      });
    }
    return converted.map((item, i) => ({
      ...item,
      color:
        chartColors.length >= i ? chartColors[i] : generateUniqueColors(1)[0]
    }));
  };

  useMemo(() => {
    if (!chartData.length) return;

    let analyticsData;

    if (
      (chartType &&
        !ANALYTICS_CHART_VIEW_TYPE.CUMULATIVE.includes(chartType)) ||
      selectedVehicleTypes?.length
    ) {
      analyticsData = calculateChartData(
        originalData as AnalyticsChartDataResponse,
        widgetType
      );
    }

    if (analyticsData) {
      setChartData(analyticsData);
    }
  }, [chartType, selectedVehicleTypes]);

  const { mutate: getChartData } = useMutation(
    (options: AnalyticsFilterApiInputPayload) => {
      const type =
        ANALYTICS_CHART_API[widgetType as keyof typeof ANALYTICS_CHART_API];
      setIsLoading(true);
      return analyticsApi.chartAnalytics(type, options);
    },
    {
      onSuccess: (data: AnalyticsChartDataResponse) => {
        setOriginalData(data);
        const analyticsData = calculateChartData(
          data as AnalyticsChartDataResponse,
          widgetType
        );
        setChartData(analyticsData);
        setIsLoading(false);
        setIsFetched(true);
      }
    }
  );

  const { mutate: getCategoryData } = useMutation(
    (options: AnalyticsCategoryApiInputPayload) => {
      const type =
        ANALYTICS_CATEGORY_API[
          widgetType as keyof typeof ANALYTICS_CATEGORY_API
        ];
      setCategoryDataLoading(true);
      return analyticsApi.categoryAnalytics(type, options);
    },
    {
      onSuccess: (data: AnalyticsCategoryDataResponse) => {
        setCategoryData(data);
        setCategoryDataLoading(false);
      },
      onError: () => {
        setCategoryDataLoading(false);
      }
    }
  );

  const { mutate: getCSVDownloadAnalytics } = useMutation(
    (options: AnalyticsCSVDownloadPayload) => {
      return analyticsApi.csvDownloadAnalytics(options);
    },
    {
      onSuccess: async (response: Response) => {
        const blob = new Blob([`${response}`], { type: "text/csv" });
        const link = document.createElement("a");
        link.href = URL.createObjectURL(blob);
        link.download = `${csvFileName}.csv`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    }
  );

  return {
    data: chartData,
    isLoading,
    isFetched,
    getChartData,
    getCategoryData,
    categoryData,
    isCategoryDataLoading,
    getCSVDownloadAnalytics
  };
};
